import { useMemo } from "react";
import { BetSlipMode, WinBoostEligibilityCondition } from "../types/enums";
import { resolveWinBoostCondition } from "../shared/utils/bonus-utils";

interface UseCalculateWinBoostProps {
  winBoosts: MappedBonus[];
  totalSelections: number;
  selections: IBetSlipSelection[];
  betBuilderOdds: Array<{ odd: number }>;
  stakes: { [key: string]: number };
  totalOdds: number;
  totalStake: number;
  mode: BetSlipMode | string;
  isFreeBetActive: boolean;
}

export const useCalculateWinBoost = ({
  winBoosts,
  totalSelections,
  selections,
  betBuilderOdds,
  stakes,
  totalOdds,
  totalStake,
  mode,
  isFreeBetActive,
}: UseCalculateWinBoostProps) => {
  return useMemo(() => {
    const eligible = winBoosts.filter(
      (b) => totalSelections >= (b.originalBonus.minSelection || 0),
    );
    const activeWinBoost = eligible.length > 0 ? eligible[eligible.length - 1] : null;

    const condition = resolveWinBoostCondition(
      activeWinBoost,
      isFreeBetActive,
      totalStake,
      totalOdds,
      selections,
      betBuilderOdds,
      stakes,
      mode as BetSlipMode,
    );

    const isWinBoostEligible = condition === WinBoostEligibilityCondition.Eligible;

    if (!isWinBoostEligible) {
      return { activeWinBoost, isWinBoostEligible: false, winBoostAmount: 0, condition };
    }

    const minStake = activeWinBoost!.originalBonus.minStakeAmount || 0;
    let potentialWin: number;

    if (mode === BetSlipMode.Single) {
      const eligibleSelections = selections.filter((sel) => {
        const stake = (stakes[sel.key] as number) || 0;
        return minStake === 0 || stake >= minStake;
      });
      const totalPayout = eligibleSelections.reduce((sum, sel) => {
        const stake = (stakes[sel.key] as number) || 0;
        return sum + stake * sel.oddPrice;
      }, 0);
      const eligibleNetTotal = eligibleSelections.reduce(
        (s: number, sel) => s + ((stakes[sel.key] as number) || 0),
        0,
      );
      potentialWin = totalPayout - eligibleNetTotal;
    } else {
      const comboNetStake = (stakes[BetSlipMode.Combo] as number) || 0;
      potentialWin = comboNetStake * totalOdds - comboNetStake;
    }

    const boostAmount = (potentialWin * activeWinBoost!.percentage) / 100;
    const winBoostAmount = Math.min(boostAmount, activeWinBoost!.amount);

    return { activeWinBoost, isWinBoostEligible: true, winBoostAmount, condition };
  }, [
    winBoosts,
    totalSelections,
    selections,
    betBuilderOdds,
    stakes,
    totalOdds,
    totalStake,
    mode,
    isFreeBetActive,
  ]);
};
